import { type Board, type Color, type Fen, type PieceType } from '@chess/api-model';
import type { Chess } from '@chess/api-chess';
import { referenceOf, type ReviewSides } from '@chess/api-review';

// Material on the board, after v1's MaterialBalance: what each side has
// lost against the full set it started with, and the balance in pawns from
// the bottom player's view. A side holding more queens than it started with
// has promoted, which is a queen gained rather than one lost.

const COMPLEMENT: Record<Exclude<PieceType, 'k'>, number> = { q: 1, r: 2, b: 2, n: 2, p: 8 };
const VALUE: Record<PieceType, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
/** The order losses are shown in: pawns first, queen last. */
export const ORDER: Array<Exclude<PieceType, 'k'>> = ['p', 'n', 'b', 'r', 'q'];

export interface Loss {
  kind: Exclude<PieceType, 'k'>;
  count: number;
}

export interface MaterialBalance {
  bottom: Color;
  bottomLost: Loss[];
  topLost: Loss[];
  /** Bottom's material minus top's, in pawns. */
  advantage: number;
  untouched: boolean;
  text: string;
}

function counts(board: Board, color: Color): Record<PieceType, number> {
  const out: Record<PieceType, number> = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
  for (const piece of Object.values(board)) {
    if (piece && piece.color === color) out[piece.type] += 1;
  }
  return out;
}

function losses(have: Record<PieceType, number>): Loss[] {
  const out: Loss[] = [];
  for (const kind of ORDER) {
    const missing = Math.max(0, COMPLEMENT[kind] - have[kind]);
    if (missing > 0) out.push({ kind, count: missing });
  }
  return out;
}

function value(have: Record<PieceType, number>): number {
  return ORDER.reduce((sum, kind) => sum + VALUE[kind] * have[kind], 0);
}

export function advantageText(advantage: number): string {
  if (advantage === 0) return 'level';
  return advantage > 0 ? `+${advantage}` : `−${-advantage}`;
}

export function materialBalance(board: Board, bottom: Color = 'white'): MaterialBalance {
  const top: Color = bottom === 'white' ? 'black' : 'white';
  const bottomHave = counts(board, bottom);
  const topHave = counts(board, top);
  const bottomLost = losses(bottomHave);
  const topLost = losses(topHave);
  const advantage = value(bottomHave) - value(topHave);
  return {
    bottom,
    bottomLost,
    topLost,
    advantage,
    untouched: bottomLost.length === 0 && topLost.length === 0,
    text: advantageText(advantage),
  };
}

/** The strip read aloud, as v1's spokenDescription. */
export function spoken(balance: MaterialBalance): string {
  const name = (c: Color) => (c === 'white' ? 'White' : 'Black');
  const top: Color = balance.bottom === 'white' ? 'black' : 'white';
  const list = (l: Loss[]) =>
    l.length === 0 ? 'nothing' : l.map((x) => `${x.count} ${PIECE_NAME[x.kind]}${x.count === 1 ? '' : 's'}`).join(', ');
  const lead = balance.advantage === 0 ? 'material is level' : `${name(balance.advantage > 0 ? balance.bottom : top)} is ${Math.abs(balance.advantage)} ${Math.abs(balance.advantage) === 1 ? 'pawn' : 'pawns'} ahead`;
  return `${name(balance.bottom)} has lost ${list(balance.bottomLost)}; ${name(top)} has lost ${list(balance.topLost)}; ${lead}`;
}

const PIECE_NAME: Record<Exclude<PieceType, 'k'>, string> = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen' };

/** Text glyph for a piece of a colour, for the trays. */
export function glyph(kind: Exclude<PieceType, 'k'>, color: Color): string {
  const white: Record<Exclude<PieceType, 'k'>, string> = { p: '♙', n: '♘', b: '♗', r: '♖', q: '♕' };
  const black: Record<Exclude<PieceType, 'k'>, string> = { p: '♟', n: '♞', b: '♝', r: '♜', q: '♛' };
  return (color === 'white' ? white : black)[kind];
}

/**
 * The balance text from the reference player's side, the reader's, whatever
 * the orientation the trays were laid out for: positive means the reader is
 * ahead, so flipping the board never changes the sign.
 */
export function balanceTextFor(balance: MaterialBalance, reference: Color): string {
  return advantageText(reference === balance.bottom ? balance.advantage : -balance.advantage);
}

/** What one player's line shows: the pieces they have lost, and their lead in pawns when they are ahead. */
export interface PlayerMaterial {
  color: Color;
  lost: Loss[];
  /** Positive, only on the line of the side that is ahead; absent when level or behind. */
  lead?: number;
}

/**
 * The material on `color`'s line: what that side has lost, grouped by kind
 * as the balance lists them, and the lead only when that side is ahead, so
 * exactly one line carries a number while the material is not level.
 */
export function materialOf(balance: MaterialBalance, color: Color): PlayerMaterial {
  const lost = color === balance.bottom ? balance.bottomLost : balance.topLost;
  const advantage = color === balance.bottom ? balance.advantage : -balance.advantage;
  return advantage > 0 ? { color, lost, lead: advantage } : { color, lost };
}

/**
 * The strip for a position on the board: the trays laid out for the board's
 * orientation, the number from the reader's side. Undefined without a
 * position, or when the rules cannot read it.
 */
export function balanceAt(chess: Pick<Chess, 'board'>, fen: Fen | undefined, sides: ReviewSides | undefined): MaterialBalance | undefined {
  if (!fen) return undefined;
  let board: Board;
  try {
    board = chess.board(fen);
  } catch {
    return undefined;
  }
  // Trays follow the board's orientation; the number is the reader's.
  const laid = materialBalance(board, sides?.orientation ?? 'white');
  return { ...laid, text: balanceTextFor(laid, referenceOf(sides)) };
}
